import { PlayerNumber } from '~/modules/players/player-number';
import { PlayerEntity, SelectedPlayerInput } from '~/modules/players/players-manager';
import { InputSource } from '~/routes/select-input/input-sources/interfaces';

export interface SlotState {
  number: PlayerNumber;
  /** What someone in the room would call this input - a phone's name, or the kind of microphone. */
  title: string;
  /** What the machine calls it - the device string, or the channel of a two-channel mic. */
  subtitle: string;
  hint: string;
  hasInput: boolean;
  /**
   * Whether a player entity exists behind the slot at all, which is not the same as having an
   * input: every player is created on `Dummy`. It matters because the roster is fixed for the
   * duration of a round - `PlayersManager.addPlayer` refuses while a song is playing - so a slot
   * with nobody behind it cannot be filled until the song is over.
   */
  hasPlayer: boolean;
  /**
   * Which microphone the slot is pointed at, in the same terms the picker encodes - or `null` when
   * it is pointed at nothing. Carried so one slot's picker can say which of the others already holds
   * an input: two slots on one channel hear the same voice twice, and until this existed nothing on
   * the input side knew that had happened. Plain values rather than the player entity, which the
   * React compiler would freeze at whatever was read off it first.
   */
  selected: SelectedPlayerInput | null;
}

const LEVEL_HINT = 'Say something — the bar should move';
const NO_INPUT_HINT = 'No input yet';

/**
 * A slot with the `Dummy` source is one nobody has claimed: every player is created with it, so it
 * means "empty", not "an input that happens to be silent". Same test `use-mic-setup-summary.ts`
 * makes for the main menu's "2 ready".
 */
export function slotState(
  number: PlayerNumber,
  player: PlayerEntity | undefined,
  input: InputSource | null,
): SlotState {
  const source = player?.input.source;

  if (!player || source === undefined || source === 'Dummy') {
    return {
      number,
      title: 'Nothing yet',
      subtitle: `Player ${number + 1} sits out until a mic is added`,
      hint: NO_INPUT_HINT,
      hasInput: false,
      hasPlayer: player !== undefined,
      selected: null,
    };
  }

  if (source === 'Remote Microphone') {
    // `input` is null when the phone that was assigned here is no longer in the connected list -
    // the slot keeps the assignment (so a reconnect lands back on it) but it cannot hear anything.
    return {
      number,
      title: player.getName(),
      subtitle: input ? 'Remote microphone · connected' : 'Remote microphone · not connected',
      hint: input ? LEVEL_HINT : 'Waiting for the phone to reconnect',
      hasInput: true,
      hasPlayer: true,
      selected: player.input,
    };
  }

  // A two-channel device is a SingStar-style pair, and its mapped label ("SingStar Wired - Blue")
  // is the name a person uses for it - so there it is the title rather than the detail.
  const multiChannel = (input?.channels ?? 1) > 1;

  return {
    number,
    title: multiChannel ? (input?.label ?? 'Microphone') : 'Built-in microphone',
    subtitle: multiChannel ? `Microphone · channel ${(input?.channel ?? 0) + 1}` : (input?.label ?? 'Microphone'),
    hint: LEVEL_HINT,
    hasInput: true,
    hasPlayer: true,
    selected: player.input,
  };
}
